import { useState } from 'react'

import { useCloseFinding } from '../api/queries'
import type { FindingSummary } from '../api/types'
import { Failure } from './Failure'

const DISPOSITIONS = [
  ['true_positive', 'True positive'],
  ['benign_positive', 'Benign — expected activity'],
  ['false_positive', 'False positive'],
  ['duplicate', 'Duplicate'],
] as const

/**
 * Closing one alert or several.
 *
 * The reason is required by the server; it is also required here, with the submit
 * button disabled until there is one, so the requirement reads as a design decision
 * rather than as an error message somebody hit.
 *
 * A bulk close writes the same reason against each alert separately rather than
 * inventing a "closed in bulk" marker. Each finding keeps its own disposition and its
 * own audit entry, because six months later the question is "why was *this* closed",
 * and "it was in a selection somebody made" is not an answer.
 */
export function CloseDialog({
  findings,
  onDone,
}: {
  findings: FindingSummary[]
  onDone: () => void
}) {
  const close = useCloseFinding()
  const [disposition, setDisposition] = useState<string>('false_positive')
  const [reason, setReason] = useState('')
  const [failed, setFailed] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const many = findings.length > 1
  const title = many
    ? `Close ${findings.length} alerts`
    : `Close: ${findings[0]?.rule_title ?? ''}`

  async function submit() {
    setBusy(true)
    setFailed(null)
    try {
      for (const finding of findings) {
        await close.mutateAsync({ id: finding.id, disposition, reason })
      }
      onDone()
    } catch (error) {
      // Partial failure is reported as partial: some of these may have closed. Saying
      // "failed" about a batch that half-succeeded would send somebody looking for
      // alerts that are no longer in the queue.
      setFailed(error)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="close-title"
      className="panel"
      style={{ position: 'fixed', inset: '20% 25% auto', zIndex: 10 }}
    >
      <h3 id="close-title" style={{ marginTop: 0 }}>
        {title}
      </h3>
      {many && (
        <p className="muted">
          Each alert is closed separately with this disposition and reason, and each
          keeps its own audit entry.
        </p>
      )}

      <label htmlFor="disposition">Disposition</label>
      <select
        id="disposition"
        value={disposition}
        onChange={(event) => setDisposition(event.target.value)}
        style={{ width: '100%', marginBottom: 'var(--space-3)' }}
      >
        {DISPOSITIONS.map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>

      <label htmlFor="reason">Why (required)</label>
      <textarea
        id="reason"
        rows={3}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        style={{ width: '100%', marginBottom: 'var(--space-3)' }}
      />

      {failed !== null && <Failure error={failed} />}

      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <button onClick={onDone}>Cancel</button>
        <button className="primary" disabled={!reason.trim() || busy} onClick={submit}>
          {busy ? 'Closing…' : many ? `Close ${findings.length} alerts` : 'Close alert'}
        </button>
      </div>
    </div>
  )
}
